import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'

const UTMFilters = ({
  utmSource,
  utmMedium,
  utmCampaign,
  onSourceChange,
  onMediumChange,
  onCampaignChange,
  onClearFilters,
  utmFilterOptions,
  patientData,
  appointmentData,
  startsData,
}) => {
  const options = useMemo(() => {
    if (utmFilterOptions) {
      return utmFilterOptions
    }

    const sources = new Set()
    const mediums = new Set()
    const campaigns = new Set()

    if (patientData) {
      Object.keys(patientData).forEach(source => {
        sources.add(source)
        const data = patientData[source]
        if (data.mediums) {
          Object.keys(data.mediums).forEach(m => mediums.add(m))
        }
        if (data.campaigns) {
          Object.keys(data.campaigns).forEach(c => campaigns.add(c))
        }
      })
    }

    if (appointmentData) {
      Object.keys(appointmentData).forEach(source => {
        sources.add(source)
        const data = appointmentData[source]
        if (data.by_medium) {
          Object.keys(data.by_medium).forEach(m => mediums.add(m))
        }
        if (data.by_campaign) {
          Object.keys(data.by_campaign).forEach(c => campaigns.add(c))
        }
      })
    }

    if (startsData) {
      startsData.forEach(start => {
        if (start.utm_source) sources.add(start.utm_source)
        if (start.utm_medium) mediums.add(start.utm_medium)
        if (start.utm_campaign) campaigns.add(start.utm_campaign)
      })
    }

    return {
      sources: [...sources].sort(),
      mediums: [...mediums].sort(),
      campaigns: [...campaigns].sort(),
    }
  }, [utmFilterOptions, patientData, appointmentData, startsData])

  const hasActiveFilters = utmSource || utmMedium || utmCampaign

  const selectSx = {
    borderRadius: 2,
    fontSize: '14px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
    },
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <FilterListIcon sx={{ color: '#7D7D7D', fontSize: 20 }} />

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Source</InputLabel>
        <Select
          value={utmSource}
          label="Source"
          onChange={e => onSourceChange(e.target.value)}
          sx={selectSx}
        >
          <MenuItem value="">
            <em>All Sources</em>
          </MenuItem>
          {options.sources.map(source => (
            <MenuItem key={source} value={source}>
              {source}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Medium</InputLabel>
        <Select
          value={utmMedium}
          label="Medium"
          onChange={e => onMediumChange(e.target.value)}
          sx={selectSx}
        >
          <MenuItem value="">
            <em>All Mediums</em>
          </MenuItem>
          {options.mediums.map(medium => (
            <MenuItem key={medium} value={medium}>
              {medium}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Campaign</InputLabel>
        <Select
          value={utmCampaign}
          label="Campaign"
          onChange={e => onCampaignChange(e.target.value)}
          sx={selectSx}
        >
          <MenuItem value="">
            <em>All Campaigns</em>
          </MenuItem>
          {options.campaigns.map(campaign => (
            <MenuItem key={campaign} value={campaign}>
              {campaign}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActiveFilters && (
        <Button
          size="small"
          startIcon={<ClearIcon />}
          onClick={onClearFilters}
          sx={{ textTransform: 'none', color: '#7D7D7D' }}
        >
          Clear filters
        </Button>
      )}
    </Box>
  )
}

export default UTMFilters
